import { useNotification as useCTNNotification } from '@ctn/ui/components/ctn-notifications';
import React, { createContext, useCallback, useContext, useMemo, type ReactElement, type ReactNode } from 'react';

import type { INotificationContext, NotificationCallback } from '@/interfaces/context/INotification.context';
import { NotificationBasic } from '@/components/Notification';

type NotificationProviderProps = {
	readonly children: ReactNode;
};

const contextDefaultValue: INotificationContext = {
	notificationSuccess: () => null,
	notificationWarning: () => null,
	notificationError: () => null,
	notificationCustom: () => null,
};

const NotificationContext = createContext<INotificationContext>(contextDefaultValue);

const NotificationProvider: React.FC<NotificationProviderProps> = ({
	children,
}: NotificationProviderProps): ReactElement => {
	const { openNotification, closeNotification } = useCTNNotification();

	const createNotification: NotificationCallback = useCallback(
		(type: 'success' | 'warning' | 'error' | 'custom') =>
			({ title, content, buttons }) => {
				openNotification({
					alignButtons: 'right',
					autoHeight: true,
					closeable: true,
					content:
						type === 'custom' ? (
							content
						) : (
							<NotificationBasic type={type} title={title as string} content={content} />
						),
					size: 'xs',
					type: 'custom',
					zIndex: 11,
					actionButtons: buttons
						? buttons(closeNotification)
						: [
								{
									key: 'accept',
									label: 'Aceptar',
									size: 'size-40',
									onClick: closeNotification,
								},
							],
				});
			},
		[openNotification, closeNotification],
	);

	const value: INotificationContext = useMemo(
		() => ({
			notificationSuccess: createNotification('success'),
			notificationWarning: createNotification('warning'),
			notificationError: createNotification('error'),
			notificationCustom: createNotification('custom'),
		}),
		[createNotification],
	);

	return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

const useNotification = (): INotificationContext => useContext(NotificationContext);

export { NotificationContext, NotificationProvider, useNotification };
